/**
 * Utility functions for generating NativeWind class names
 */

import { Platform } from "react-native";

/**
 * Combines multiple class names, filtering out falsy values
 * @param classes - Variable number of class name strings or falsy values to combine
 * @returns Space-separated string of truthy class names
 */
export const cn = (...classes: (string | undefined | false)[]) => {
  return classes.filter(Boolean).join(" ");
};

/**
 * Platform-specific class names
 */
export const platformClasses = {
  ios: (className: string) => (Platform.OS === "ios" ? className : ""),
  android: (className: string) => (Platform.OS === "android" ? className : ""),
  web: (className: string) => (Platform.OS === "web" ? className : ""),
  native: (className: string) => (Platform.OS !== "web" ? className : ""),
};

/**
 * Status-based color classes
 * @remarks SE-6800b: disconnected's bg-gray-600 -> bg-surface-action
 * (worst=53, Δlight=[-31,-41,-53], Δdark=[-31,-41,-53]).
 */
export const statusColors = {
  connected: "bg-status-success text-content-on-accent",
  connecting: "bg-primary-material text-white",
  disconnected: "bg-surface-action text-white",
  error: "bg-status-error text-content-on-accent",
  reconnecting: "bg-status-reconnecting text-white",
};

/**
 * Message bubble styles
 * @remarks SE-6800b: assistant's bg-gray-50 -> bg-surface-subtle (role/rung
 * override to keep it visually lighter than the system bubble below; the
 * numeric winner bg-surface-strong (worst=135) would have collapsed both
 * bubbles onto the identical token — worst=187, Δlight=[-7,-9,-10],
 * Δdark=[-184,-186,-187]). text-gray-900 -> role override
 * text-content-primary (numeric winner was family-wrong
 * text-status-success; worst=228, Δlight=[21,14,0], Δdark=[228,221,206]).
 * system's bg-gray-200 -> bg-surface-strong (worst=119, Δlight=[-16,-19,-23],
 * Δdark=[-113,-115,-119]); text-gray-600 -> text-content-muted (worst=88,
 * Δlight=[65,55,41], Δdark=[88,78,64]).
 */
export const messageBubbleClasses = {
  user: "bg-primary text-white rounded-lg px-4 py-3 max-w-80",
  assistant:
    "bg-surface-subtle text-content-primary rounded-lg px-4 py-3 max-w-80",
  system: "bg-surface-strong text-content-muted rounded-md px-3 py-2 max-w-80",
};

/**
 * Generates button classes based on disabled and pressed states
 * @param disabled - Whether the button is in a disabled state
 * @param pressed - Whether the button is currently being pressed (optional)
 * @returns Tailwind class string for button styling based on current state
 */
export const buttonStateClasses = (disabled: boolean, pressed?: boolean) => {
  // SE-6800b: bg-gray-400 -> bg-surface-strong (worst=59, Δlight=[57,49,37],
  // Δdark=[-40,-47,-59]).
  return cn(
    "rounded-md px-4 py-2 min-w-16",
    disabled ? "bg-surface-strong opacity-50" : "bg-primary",
    pressed && !disabled && "opacity-80",
    !disabled && "active:opacity-80"
  );
};

/**
 * Agent status classes
 */
export const agentStatusClasses = {
  pending: "opacity-50",
  running:
    "opacity-100 bg-background-light-blue border-l-3 border-primary-material",
  complete: "opacity-70 bg-white border-l-3 border-status-success",
  error: "opacity-100 bg-background-light-red border-l-3 border-status-error",
};

/**
 * Generates container classes with platform-specific padding based on variant
 * @param variant - The container variant type (default, modal, or overlay)
 * @returns Tailwind class string for container styling with platform-specific adjustments
 */
export const containerClasses = (
  variant: "default" | "modal" | "overlay" = "default"
) => {
  // SE-6800b: default's bg-gray-50 -> bg-surface-strong (worst=135,
  // Δlight=[-36,-38,-39], Δdark=[-133,-134,-135]).
  const baseClasses = {
    default: "flex-1 bg-surface-strong",
    modal: "bg-white rounded-md p-5",
    overlay:
      "absolute inset-0 bg-background-overlay justify-center items-center z-modal",
  };

  return cn(
    baseClasses[variant],
    variant === "default" && platformClasses.ios("pb-3"),
    variant === "default" && platformClasses.android("pb-3"),
    variant === "default" && platformClasses.web("pb-3")
  );
};
